import { LESSONS, type Lesson } from "./course";

/**
 * Which Fundamentals lessons an account has finished. The backend keeps the
 * authoritative record; browser storage supports migration and offline use.
 */

export const progressStorageKey = (memberId: string) => `noticeboard.courseProgress.${memberId}`;

/** When the Team member finished every lesson, as "YYYY-MM-DD": the date on their certificate. */
export const finishedOnStorageKey = (memberId: string) =>
  `noticeboard.courseFinishedOn.${memberId}`;

/** Today in the browser's time zone, as "YYYY-MM-DD". */
export const localDate = (day: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
};

/** A stored finish date, if it reads as one. */
export const parseFinishedOn = (stored: string | null): string | undefined =>
  stored && /^\d{4}-\d{2}-\d{2}$/.test(stored) ? stored : undefined;

/** The finished lessons' slugs from storage. A removed lesson, or anything unreadable, counts for nothing. */
export const parseFinished = (stored: string | null): Set<string> => {
  try {
    const slugs: unknown = JSON.parse(stored ?? "[]");
    if (!Array.isArray(slugs)) return new Set();
    return new Set(LESSONS.map((l) => l.slug).filter((slug) => slugs.includes(slug)));
  } catch {
    return new Set();
  }
};

export type CourseProgress = {
  finished: Set<string>;
  /** Out of the lessons written so far. */
  doneCount: number;
  percentDone: number;
  /** The first unfinished lesson; none once every written lesson is done. */
  nextUp: Lesson | undefined;
};

export const courseProgress = (finished: Set<string>): CourseProgress => {
  const doneCount = LESSONS.filter((l) => finished.has(l.slug)).length;
  return {
    finished,
    doneCount,
    percentDone: Math.round((doneCount / LESSONS.length) * 100),
    nextUp: LESSONS.find((l) => !finished.has(l.slug)),
  };
};
